import { ReactNode } from "react";

import { cn } from "../utls/utils";
import Typography from "./typography";

export type ThemedInfoSectionAccent = "terracotta" | "beige" | "cream";

const accentClasses: Record<ThemedInfoSectionAccent, string> = {
  terracotta: "border-terracotta-300 bg-cream-50",
  beige: "border-beige-300 bg-beige-50",
  cream: "border-cream-200 bg-white",
};

const titleColorClasses: Record<ThemedInfoSectionAccent, string> = {
  terracotta: "text-terracotta-700",
  beige: "text-brown-700",
  cream: "text-brown-700",
};

const eyebrowColorClasses: Record<ThemedInfoSectionAccent, string> = {
  terracotta: "text-terracotta-600",
  beige: "text-brown-500",
  cream: "text-brown-500",
};

const headerBackgroundClasses: Record<ThemedInfoSectionAccent, string> = {
  terracotta: "border-b border-cream-200 bg-cream-100",
  beige: "border-b border-beige-200 bg-beige-100",
  cream: "border-b border-cream-200 bg-cream-50",
};

export interface ThemedInfoSectionProps {
  /** Title displayed in the section header. */
  title: string;
  /** Optional supporting description below the title. */
  description?: ReactNode;
  /** Optional action area rendered to the right of the title. */
  actions?: ReactNode;
  /** Main content of the section. */
  children: ReactNode;
  /** Accent color for the left border and header text. */
  accent?: ThemedInfoSectionAccent;
  /** Additional className overrides. */
  className?: string;
  /** Optional caption rendered above the title. */
  eyebrow?: ReactNode;
}

export function ThemedInfoSection({
  title,
  description,
  actions,
  children,
  accent = "terracotta",
  className,
  eyebrow,
}: ThemedInfoSectionProps) {
  return (
    <section
      className={cn(
        "overflow-hidden rounded-xl border-l-4 shadow-sm",
        accentClasses[accent],
        className
      )}
    >
      <div
        className={cn(
          "flex flex-col gap-4 px-6 py-5 sm:flex-row sm:items-start sm:justify-between",
          headerBackgroundClasses[accent]
        )}
      >
        <div className="space-y-2">
          {eyebrow ? (
            <Typography
              variant="overline"
              className={eyebrowColorClasses[accent]}
            >
              {eyebrow}
            </Typography>
          ) : null}
          <Typography variant="h4" className={titleColorClasses[accent]}>
            {title}
          </Typography>
          {description ? (
            <Typography variant="body2" className="text-brown-600">
              {description}
            </Typography>
          ) : null}
        </div>
        {actions ? (
          <div className="flex flex-shrink-0 items-center gap-2">{actions}</div>
        ) : null}
      </div>
      <div className="bg-white px-6 py-5">{children}</div>
    </section>
  );
}

export default ThemedInfoSection;
